/**
 * Module 'project-protocol', hub side: what the hub module adds to the protocol types in
 * ./project-protocol.ts (check settings validation, the agent's CPU report, audit labels,
 * deep links). Kept apart so the readers and the hub can change their parts independently.
 */
import type { Ts } from '../index.ts';
import { DEFAULT_NATIVE_CHECK_SETTINGS } from './project-protocol.ts';
import type { CpuConsumer, NativeCheckSettings, ProjectBusy } from './project-protocol.ts';

/** NativeCheckSettings minutes and percentages: whole numbers in these ranges. */
export const NATIVE_CHECK_LIMITS: Record<Exclude<keyof NativeCheckSettings, 'alwaysConfirmActions' | 'autoFix' | 'autoFixBy' | 'stopIdleDevServers'>, [number, number]> = {
  sessionWaitingWarnMin: [1, 1440],
  sessionWaitingCriticalMin: [1, 1440],
  workflowStalledWarnMin: [1, 1440],
  workflowStalledCriticalMin: [1, 1440],
  worktreeOrphanedWarnMin: [1, 10080],
  cpuWarnPct: [50, 100],
  cpuWarnMin: [1, 120],
  cpuCriticalPct: [50, 100],
  cpuCriticalMin: [1, 120],
  autoFixAfterMin: [1, 1440],
  autoFixMaxAttempts: [1, 10],
};

/** Why a PUT /settings body cannot be used, in words, or null. Every field is optional; unknown fields are refused. */
export function nativeCheckSettingsError(v: unknown): string | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return 'Send the settings as a JSON object.';
  const o = v as Record<string, unknown>;
  for (const [k, x] of Object.entries(o)) {
    if (k === 'alwaysConfirmActions' || k === 'autoFix' || k === 'stopIdleDevServers') {
      if (typeof x !== 'boolean') return `${k} is true or false.`;
      continue;
    }
    if (k === 'autoFixBy') return 'autoFixBy is set by the hub: it is whoever turns autoFix on.';
    const range = NATIVE_CHECK_LIMITS[k as keyof typeof NATIVE_CHECK_LIMITS];
    if (!range) return `${k} is not a check setting.`;
    if (typeof x !== 'number' || !Number.isInteger(x) || x < range[0] || x > range[1]) return `${k} must be a whole number from ${range[0]} to ${range[1]}.`;
  }
  const s = { ...DEFAULT_NATIVE_CHECK_SETTINGS, ...o } as NativeCheckSettings;
  if (s.sessionWaitingCriticalMin < s.sessionWaitingWarnMin) return 'A session waits longer for critical than for warning.';
  if (s.workflowStalledCriticalMin < s.workflowStalledWarnMin) return 'A workflow stalls longer for critical than for warning.';
  if (s.cpuCriticalPct < s.cpuWarnPct) return 'The critical CPU level is at or above the warning level.';
  return null;
}

/**
 * Who uses the CPU of one machine (agent module 'cpu-top', sent under ext 'project-protocol' as
 * 'cpu-top'). The agent samples only while the machine is busy, at most every 15 s, and sends an
 * empty list once when it is no longer busy. (Added by the hub module, 17 Oct 2026.)
 */
export interface CpuTopReport {
  at: Ts;
  /** Machine busy fraction when sampled, 0..1. */
  load: number;
  /** Over how long the shares were measured. */
  windowMs: number;
  /** Most first, at most CPU_TOP_MAX; pct is a share of the whole machine (0..100). */
  consumers: CpuConsumer[];
}
export const CPU_TOP_FRAME = 'cpu-top';
export const CPU_TOP_MAX = 8;
/** The agent samples processes at most this often. */
export const CPU_TOP_MIN_GAP_MS = 15_000;
/** ... and only while the machine is at least this busy (0..1). */
export const CPU_TOP_LOAD_GATE = 0.8;

/** Audit labels for this module's own actions. */
export const PROJECT_PROTOCOL_AUDIT_LABEL: Record<string, string> = {
  'project.action.request': 'Request project action',
  'project.action.confirm': 'Confirm project action',
  'project.action.cancel': 'Cancel project action',
  'project.action.done': 'Project action finished',
  'project.dispatch': 'Dispatch a fix',
  'project.yield.start-now': 'Start waiting work now',
  'project.settings': 'Change check settings',
};

/** The console's deep link to one problem (notifications use it): the problem drawer, over the Overview. */
export function problemHref(key: string): string {
  return `/?problem=${encodeURIComponent(key)}`;
}

/** "Waits for Qawl: landing 5 submissions", for holds and the scheduler. */
export function yieldText(projectName: string, busy: Pick<ProjectBusy, 'what'>): string {
  return `Waits for ${projectName}: ${busy.what}`;
}
